
import React from 'react';
import { Separator } from '@/components/ui/separator';
import { Product } from '@/lib/supabase/products/types';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ProductDetailsProps {
  product: Product;
}

const ProductDetails = ({ product }: ProductDetailsProps) => {
  return (
    <div className="space-y-6">
      {/* Description */}
      {product.description && (
        <div>
          <h2 className="text-lg font-medium">Description</h2>
          <div 
            className="mt-2 text-sm text-gray-700 dark:text-gray-300"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />
        </div>
      )}

      {/* Avantages du produit */}
      {product.benefits && product.benefits.length > 0 && (
        <>
          <Separator />
          <div>
            <h2 className="text-lg font-medium">Avantages</h2>
            <div className="mt-4 space-y-4">
              {product.benefits
                .filter(benefit => benefit.visible !== false)
                .map((benefit, index) => (
                  <div key={index} className="flex gap-3">
                    {benefit.icon && (
                      <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                        <span className="text-lg">{benefit.icon}</span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium">{benefit.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{benefit.description}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}

      {/* FAQ */}
      {product.faqs && product.faqs.length > 0 && (
        <>
          <Separator />
          <div>
            <h2 className="text-lg font-medium mb-4">Questions fréquentes</h2>
            <Accordion type="single" collapsible className="w-full">
              {product.faqs
                .filter(faq => faq.visible !== false)
                .map((faq, index) => (
                  <AccordionItem key={index} value={`faq-${index}`}>
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent>{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
            </Accordion>
          </div>
        </>
      )}

      {/* Détails */}
      <Separator />
      <div>
        <h2 className="text-lg font-medium">Détails</h2>
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {product.category && (
            <>
              <div className="font-medium">Catégorie</div>
              <div>{product.category}</div>
            </>
          )}
          
          {product.is_stock_managed && (
            <>
              <div className="font-medium">Stock</div>
              <div>{product.stock_quantity} disponible(s)</div>
            </>
          )}
          
          {product.sku && (
            <>
              <div className="font-medium">SKU</div>
              <div>{product.sku}</div>
            </>
          )}

          {product.weight && (
            <>
              <div className="font-medium">Poids</div>
              <div>{product.weight} g</div>
            </>
          )}

          {product.dimensions && (
            <>
              <div className="font-medium">Dimensions</div>
              <div>
                {product.dimensions.length && product.dimensions.width && product.dimensions.height
                  ? `${product.dimensions.length} × ${product.dimensions.width} × ${product.dimensions.height} cm`
                  : 'Non spécifiées'}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tags */}
      {product.tags && product.tags.length > 0 && (
        <>
          <Separator />
          <div>
            <h2 className="text-lg font-medium">Tags</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {product.tags.map((tag, index) => (
                <Badge key={index} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductDetails;
